import {compose} from "@reduxjs/toolkit";

import * as actionTypes from "../constants/actionTypes";
import * as methods from "../constants/methods";
import * as localConfig from "../constants/localConfig";
import * as config from "../constants/config";
import * as componentTypes from "../constants/componentTypes";
import * as actions from "../actions/serverActions";
import * as rccActions from "../actions/rccActions";
import * as helperActions from "../actions/helperActions";
import * as windowService from "../services/windowService";
import * as getterHelper from "../helpers/getterHelper";
import * as formatHelper from "../helpers/formatHelper";

const socketMiddleware = () => {
    /**
     * WebSocket for communication with Redlike server.
     *
     * @type WebSocket
     */
    let serverSocket = null;

    /**
     * WebSocket for communication with Redlike control center.
     *
     * @type WebSocket
     */
    let rccSocket = null;

    /**
     * WebSocket for communication with Redlike Helper Server.
     *
     * @type WebSocket
     */
    let helperSocket = null;

    const initServerSocket = ({dispatch}, url) => {
        if (serverSocket === null || serverSocket.readyState === 3) {
            serverSocket = new WebSocket(url);
            serverSocket.onopen = compose(dispatch, actions.websocketOpened);
            serverSocket.onclose = compose(dispatch, actions.websocketClosed);
            serverSocket.onmessage = compose(dispatch, actions.websocketResponse);
            serverSocket.onerror = function () {
                windowService.logInfo("Server websocket error.");
            };
            dispatch(actions.websocketConnecting());
        }
    };

    const closeServerSocket = () => {
        if (serverSocket !== null) {
            serverSocket.close();
            serverSocket = null;
        }
    };

    const initRccSocket = (store, url) => {
        if (rccSocket === null || rccSocket.readyState === 3) {
            rccSocket = new WebSocket(url);
            rccSocket.onopen = compose(store.dispatch, rccActions.websocketOpened);
            rccSocket.onclose = compose(store.dispatch, rccActions.websocketClosed);
            rccSocket.onmessage = compose(store.dispatch, rccActions.websocketResponse);
            rccSocket.onerror = compose(store.dispatch, rccActions.websocketError);
            store.dispatch(rccActions.websocketConnecting());
            setTimeout(() => {
                if (!store.getState().rcc.connected) {
                    windowService.logInfo("RCC websocket connection timeout to " + store.getState().rcc.url + ".");
                    store.dispatch(rccActions.websocketError());
                }
            }, config.RCC_RECONNECT_TIMEOUT);
        }
    };

    const closeRccSocket = () => {
        if (rccSocket !== null) {
            rccSocket.close();
            rccSocket = null;
        }
    };

    const initHelperSocket = ({dispatch}, url) => {
        if (helperSocket === null || helperSocket.readyState === 3) {
            helperSocket = new WebSocket(url);
            helperSocket.onopen = compose(dispatch, helperActions.websocketOpened);
            helperSocket.onclose = compose(dispatch, helperActions.websocketClosed);
            helperSocket.onmessage = compose(dispatch, helperActions.websocketResponse);
            helperSocket.onerror = function () {
                windowService.logInfo("Helper websocket error.");
            };
            dispatch(helperActions.websocketConnecting());
        }
    };

    const closeHelperSocket = () => {
        if (helperSocket !== null) {
            helperSocket.close();
            helperSocket = null;
        }
    };

    const canBeRunAnytime = (method) => {
        if (
            method === methods.METHOD_RCC_GET_VERSION ||
            method === methods.METHOD_GET_DRIVER_TYPES ||
            method === methods.METHOD_GET_DRIVER_CONFIGURATION ||
            method === methods.METHOD_SET_DRIVER_CONFIGURATION ||
            method === methods.METHOD_MACHINE_GET_CONFIGURATION ||
            method === methods.METHOD_MACHINE_SET_CONFIGURATION ||
            method === methods.METHOD_MACHINE_GET_ERROR_DETAILS ||
            method === methods.METHOD_MACHINE_GET_ERROR_IMAGE ||
            method === methods.METHOD_MACHINE_GET_CANISTER_IMAGE ||
            method === methods.METHOD_MACHINE_IS_SELECTED ||
            method === methods.METHOD_MACHINE_IS_CONNECTING ||
            method === methods.METHOD_MACHINE_IS_CONNECTED ||
            method === methods.METHOD_MACHINE_IS_BUSY ||
            method === methods.METHOD_RCC_PRINT_FORMULA_SILENT ||
            method === methods.METHOD_MACHINE_GET_UNITS ||
            method === methods.METHOD_PREPARE_FILE ||
            method === methods.METHOD_PREPARE_FILE_BYTES ||
            method === methods.METHOD_RELEASE_PREPARED_FILE
        ) {
            return true;
        }

        return false;
    };

    /**
     * Checks allowed method for currently connected machine.
     *
     * @param   {Object}    machine
     * @param   {String}    method
     * @returns {Boolean}
     */
    const allowedRequest = (machine, method) => {
        if (canBeRunAnytime(method)) {
            return true;
        }

        if (method === methods.METHOD_MACHINE_GET_DIALOG || method === methods.METHOD_MACHINE_DIALOG_BUTTON_CLICK) {
            if (machine.isSelected) {
                return true;
            }

            return false;
        }

        if (!machine.isConnected) {
            if (method === methods.METHOD_MACHINE_START_CONNECT) {
                return true;
            }

            return false;
        }

        if (method === methods.METHOD_MACHINE_START_DISCONNECT || method === methods.METHOD_MACHINE_GET_CALLS) {
            return true;
        }

        return machine.validMethod(method);
    };

    /**
     * Checks allowed method for currently connected spectro.
     *
     * @param   {Object}    spectro
     * @param   {String}    method
     * @returns {Boolean}
     */
    const allowedSpectroRequest = (spectro, method) => {
        if (
            method === methods.METHOD_SPECTRO_IS_SELECTED ||
            method === methods.METHOD_SPECTRO_IS_CONNECTED ||
            method === methods.METHOD_SPECTRO_START_CONNECT ||
            method === methods.METHOD_SPECTRO_GET_DIALOG ||
            method === methods.METHOD_SPECTRO_DIALOG_BUTTON_CLICK ||
            method === methods.METHOD_GET_SPECTRO_CONFIGURATION ||
            method === methods.METHOD_SET_SPECTRO_CONFIGURATION ||
            method === methods.METHOD_GET_SPECTRO_TYPES ||
            method === methods.METHOD_GET_SPECTRO_XRITE_MODELS ||
            method === methods.METHOD_GET_SPECTRO_COM_PORTS ||
            method === methods.METHOD_SPECTRO_START_DISCONNECT
        ) {
            return true;
        }

        if (spectro === null) {
            return false;
        }

        if (method === methods.METHOD_SPECTRO_GET_CALLS) {
            return true;
        }

        return spectro.validMethod(method);
    };

    return (store) => (next) => (action) => {
        if (window.storeProvider.isDebugMode()) {
            console.info(action);
        }

        switch (action.type) {
            case actionTypes.SERVER_REQUEST:
                next(actions.processWebsocketRequest(action.tabId, action.payload, action.method, action.next));
                break;
            case actionTypes.WEBSOCKET_REQUEST:
                if (serverSocket !== null && serverSocket.readyState === 1) {
                    let request = formatHelper.escapeJsonRequest(action.payload);

                    windowService.logRequest("Server", request);
                    serverSocket.send(request);

                    window.storeProvider.resetLastServerActivity();
                }

                next(action);
                break;
            case actionTypes.WEBSOCKET_PING:
                if (serverSocket !== null && serverSocket.readyState === 1) {
                    windowService.logDebug("Server request : PING");
                    serverSocket.send(methods.METHOD_PING);
                }

                next(action);
                break;
            case actionTypes.RCC_REQUEST:
                if (
                    store.getState().rcc.connected &&
                    allowedRequest(store.getState().rcc.machine, action.payload.type)
                ) {
                    next(rccActions.processWebsocketRequest(action.tabId, action.payload, action.method, action.safe));
                }

                break;
            case actionTypes.RCC_WEBSOCKET_REQUEST:
                if (rccSocket && rccSocket.readyState === 1) {
                    let rccRequest = formatHelper.escapeJsonRequest(action.payload);
                    windowService.logRequest("RCC   ", rccRequest);
                    rccSocket.send(rccRequest);
                }

                next(action);
                break;
            case actionTypes.HELPER_REQUEST:
                if (store.getState().helper.connected) {
                    next(helperActions.processWebsocketRequest(action.tabId, action.payload, action.method));
                }

                break;
            case actionTypes.HELPER_WEBSOCKET_REQUEST:
                if (helperSocket && helperSocket.readyState === 1) {
                    let helperRequest = JSON.stringify(action.payload);

                    windowService.logRequest("Helper", helperRequest);
                    helperSocket.send(helperRequest);
                }

                next(action);
                break;
            case actionTypes.RCC_SPECTRO_REQUEST:
                if (
                    store.getState().rcc.connected &&
                    allowedSpectroRequest(store.getState().spectro.spectro, action.payload.type)
                ) {
                    next(
                        rccActions.processSpectroWebsocketRequest(
                            action.tabId,
                            action.payload,
                            action.method,
                            action.safe,
                            action.safer,
                            action.next
                        )
                    );
                }

                break;
            case actionTypes.RCC_SIMPLE_REQUEST:
                if (store.getState().rcc.connected) {
                    next(
                        rccActions.processSimpleWebsocketRequest(
                            action.tabId,
                            action.payload,
                            action.method,
                            action.next
                        )
                    );
                }

                break;
            case actionTypes.SERVER_CONNECT:
                closeServerSocket();
                initServerSocket(store, action.payload.url);
                next(action);
                break;
            case actionTypes.SERVER_RECONNECT:
                closeServerSocket();

                setTimeout(() => {
                    initServerSocket(store, action.payload.url);
                }, config.SERVER_RECONNECT_TIMEOUT);

                next(action);
                break;
            case actionTypes.WEBSOCKET_CLOSED:
                closeServerSocket();

                if (store.getState().server.sequence !== config.SERVER_DISCONNECT_SEQUENCE) {
                    setTimeout(() => {
                        if (!store.getState().server.connected && store.getState().server.activeServer !== null) {
                            initServerSocket(store, store.getState().server.activeServer.getWebSocketUrl());
                        }
                    }, config.SERVER_RECONNECT_TIMEOUT);
                }

                next(action);
                break;
            case actionTypes.SERVER_DISCONNECT:
                closeServerSocket();
                break;
            case actionTypes.RCC_CONNECT:
                closeRccSocket();
                initRccSocket(store, action.payload.url);
                break;
            case actionTypes.RCC_WEBSOCKET_CLOSED:
                closeRccSocket();

                if (
                    store.getState().navigation.activeMenuItem !== componentTypes.MENU_RCC_CONNECTION &&
                    !windowService.isBrowser()
                ) {
                    setTimeout(() => {
                        if (!store.getState().rcc.connected) {
                            initRccSocket(
                                store,
                                config.WEBSOCKET_PROTOCOL + store.getState().rcc.url + ":" + config.RCC_PORT
                            );
                        }
                    }, config.RCC_RECONNECT_TIMEOUT);
                }

                next(action);
                break;
            case actionTypes.RCC_DISCONNECT:
                closeRccSocket();
                break;
            case actionTypes.HELPER_CONNECT:
                closeHelperSocket();

                if ((windowService.isBrowser() && localConfig.CONNECT_HELPER) || !windowService.isBrowser()) {
                    initHelperSocket(store, action.payload.url);
                }

                next(action);
                break;
            case actionTypes.HELPER_WEBSOCKET_CLOSED:
                closeHelperSocket();

                if ((windowService.isBrowser() && localConfig.CONNECT_HELPER) || !windowService.isBrowser()) {
                    setTimeout(() => {
                        if (!store.getState().helper.connected) {
                            initHelperSocket(
                                store,
                                getterHelper.getUrlByIpAndPort(localConfig.DEFAULT_HELPER, config.HELPER_PORT)
                            );
                        }
                    }, config.HELPER_RECONNECT_TIMEOUT);
                }

                next(action);
                break;
            case actionTypes.HELPER_DISCONNECT:
                closeHelperSocket();
                break;
            default:
                next(action);
        }
    };
};

export default socketMiddleware();
